import {
  createStore,
  applyMiddleware,
  Action,
  CombinedState,
  Middleware,
  PreloadedState,
  Reducer,
  ReducersMapObject,
  StoreEnhancer,
} from 'redux';
import {
  composeWithDevTools,
  EnhancerOptions,
} from 'redux-devtools-extension/developmentOnly';
import { ExtendedStore } from './checkStore';
import { createSagaMiddleware } from '../saga/sagaMiddleware';
import { createReducerCreator } from './createReducerCreator';
import { StoreEnvOptions } from './getStoreEnvOptions';

const sagaMiddleware = createSagaMiddleware();

/**
 * Create store creator by environment options
 *
 * @name createStoreCreator
 * @function
 * @param {StoreEnvOptions} storeEnvOptions
 */

export const createStoreCreator =
  ({ combineReducers }: StoreEnvOptions) =>
  /**
   * Create redux store with possibility to inject reducers and sagas
   *
   * @name createStore
   * @function
   * @param {Object} [predefinedReducers = {}] - an object, which describes an application reducers
   * @param {*} [initialState = {}] - the initial state of application
   * @param {import('redux').Middleware[]} middleware - middleware which are used in application
   * @param {import('redux').StoreEnhancer[]} [customEnhancers = [] - an array of custom enhancers
   * @param {import('redux-devtools-extension').EnhancerOptions} reduxDevToolsEnhancerOptions
   * @returns {import('redux').Store}
   */
  <S, A extends Action, Ext, StateExt>(
    predefinedReducers?: ReducersMapObject<S, $TsFixMe>,
    initialState: PreloadedState<S> = {} as PreloadedState<S>,
    middleware: Middleware[] = [],
    customEnhancers: StoreEnhancer<Ext, StateExt>[] = [],
    reduxDevToolsEnhancerOptions: EnhancerOptions = {},
  ) => {
    const enhancers = [
      applyMiddleware(...middleware, sagaMiddleware),
      ...customEnhancers,
    ];

    // If Redux DevTools Extension is installed use it, otherwise use Redux compose
    const composeEnhancers = composeWithDevTools({
      // Prevent recomputing reducers for `replaceReducer`
      shouldHotReload: false,
      ...reduxDevToolsEnhancerOptions,
    });

    const createReducer = createReducerCreator(combineReducers, predefinedReducers);

    const store: ExtendedStore<S, A> = createStore(
      createReducer(predefinedReducers) as unknown as Reducer<CombinedState<S>, A>,
      initialState,
      composeEnhancers(...enhancers),
    );

    // Extensions
    store.runSaga = sagaMiddleware.run.bind(sagaMiddleware);
    store.injectedReducers = {}; // Reducer registry
    store.injectedSagas = {}; // Saga registry
    store.createReducer = createReducer;

    return store;
  };
